import { useEffect, useState } from "react";
import {
  Anchor,
  Button,
  Card,
  Container,
  Group,
  Loader,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Tabs,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconDownload } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { CustomerStatsTab } from "../components/shipments/CustomerStatsTab";
import { DeliveryPlanTab } from "../components/shipments/DeliveryPlanTab";
import { ApiError, api } from "../api/client";
import type { Customer, ShipmentStatsResponse } from "../api/types";
import { deliveryTypeLabel } from "../lib/shipmentLabels";
import { ResponsiveTable } from "../components/responsiveTable/ResponsiveTable";

function firstOfMonthStr(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}-01`;
}

function endOfMonthStr(): string {
  const d = new Date();
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  const y = last.getFullYear();
  const m = String(last.getMonth() + 1).padStart(2, "0");
  const dd = String(last.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

export function ShipmentStats() {
  const { t } = useTranslation();
  const [from, setFrom] = useState<string>(firstOfMonthStr());
  const [to, setTo] = useState<string>(endOfMonthStr());
  const [dateField, setDateField] = useState<"shipped" | "created">("shipped");
  const [customerId, setCustomerId] = useState<string>("all");
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [data, setData] = useState<ShipmentStatsResponse | null>(null);
  const [loading, setLoading] = useState(false);

  async function loadCustomers() {
    try {
      const list = await api.listCustomers();
      setCustomers(list);
    } catch {
      // soft-fail
    }
  }

  async function reload() {
    if (!from || !to) return;
    setLoading(true);
    try {
      const r = await api.shipmentStats({
        from,
        to,
        date_field: dateField,
        customer_id: customerId === "all" || customerId === "null" ? customerId : Number(customerId),
      });
      setData(r);
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void loadCustomers(); }, []);
  useEffect(() => { void reload(); }, [from, to, dateField, customerId]);

  function exportXlsx() {
    if (!from || !to) return;
    const url = api.shipmentStatsExportUrl({
      from,
      to,
      date_field: dateField,
      customer_id: customerId === "all" || customerId === "null" ? customerId : Number(customerId),
    });
    window.location.href = url;
  }

  const customerOptions = [
    { value: "all", label: t("shipmentStats.allCustomers") },
    { value: "null", label: t("shipmentStats.internalStockOption") },
    ...customers.map((c) => ({ value: String(c.id), label: c.name })),
  ];

  return (
    <Container size="lg">
      <Title order={3} mb="md">{t("shipmentStats.title")}</Title>

      <Tabs defaultValue="stats">
        <Tabs.List mb="md">
          <Tabs.Tab value="stats">{t("shipmentStats.plan.tabStats")}</Tabs.Tab>
          <Tabs.Tab value="plan">{t("shipmentStats.plan.tabPlan")}</Tabs.Tab>
          <Tabs.Tab value="by-customer">{t("shipmentStats.customerStats.tab")}</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="stats">
      <Card withBorder mb="md">
        <Group align="end" gap="sm" wrap="wrap">
          <TextInput
            type="date"
            label={t("shipmentStats.from")}
            value={from}
            onChange={(e) => setFrom(e.currentTarget.value)}
            w={160}
          />
          <TextInput
            type="date"
            label={t("shipmentStats.to")}
            value={to}
            onChange={(e) => setTo(e.currentTarget.value)}
            w={160}
          />
          <Select
            label={t("shipmentStats.dateField")}
            data={[
              { value: "shipped", label: t("shipmentStats.dateFieldShipped") },
              { value: "created", label: t("shipmentStats.dateFieldCreated") },
            ]}
            value={dateField}
            onChange={(v) => setDateField((v as "shipped" | "created") ?? "shipped")}
            allowDeselect={false}
            w={200}
          />
          <Select
            label={t("shipmentStats.customer")}
            data={customerOptions}
            value={customerId}
            onChange={(v) => setCustomerId(v ?? "all")}
            searchable
            allowDeselect={false}
            w={240}
          />
          <Button
            variant="default"
            leftSection={<IconDownload size={14} />}
            onClick={exportXlsx}
            disabled={!data}
          >
            {t("shipmentStats.exportXlsx")}
          </Button>
        </Group>
      </Card>

      {loading && <Loader />}

      {data && !loading && (
        <Stack gap="md">
          <SimpleGrid cols={{ base: 1, sm: 4 }}>
            <StatCard label={t("shipmentStats.shipments")} value={String(data.totals.shipments)} />
            <StatCard label={t("shipmentStats.devices")} value={String(data.totals.devices)} />
            <StatCard
              label={t("shipmentStats.defects")}
              value={String(data.totals.defects)}
              color={data.totals.defects > 0 ? "red" : undefined}
            />
            <StatCard
              label={t("shipmentStats.returns")}
              value={String(data.totals.returns)}
              color={data.totals.returns > 0 ? "orange" : undefined}
            />
          </SimpleGrid>

          <Card withBorder>
            <Title order={5} mb="xs">{t("shipmentStats.internalStockTitle")}</Title>
            <Text size="xs" c="dimmed" mb="xs">{t("shipmentStats.internalStockHint")}</Text>
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <StatCard label={t("shipmentStats.internalShipments")} value={String(data.internal_stock.shipments)} />
              <StatCard label={t("shipmentStats.devicesUnassigned")} value={String(data.internal_stock.devices_unassigned)} />
            </SimpleGrid>
          </Card>

          <Card withBorder>
            <Title order={5} mb="xs">{t("shipmentStats.byStatus")}</Title>
            <SimpleGrid cols={{ base: 2, sm: 4 }}>
              <StatCard label={t("shipmentStats.statusDraft")} value={String(data.by_status.draft)} />
              <StatCard label={t("shipmentStats.statusReady")} value={String(data.by_status.ready)} />
              <StatCard label={t("shipmentStats.statusShipped")} value={String(data.by_status.shipped)} />
              <StatCard label={t("shipmentStats.statusDelivered")} value={String(data.by_status.delivered)} />
            </SimpleGrid>
          </Card>

          {data.by_delivery_type.length > 0 && (
            <Card withBorder>
              <Title order={5} mb="xs">{t("shipmentStats.byDeliveryType")}</Title>
              <ResponsiveTable
                mobile={{
                  rows: data.by_delivery_type,
                  rowKey: (r) => `${r.slug ?? ""}-${r.name ?? ""}`,
                  title: (r) => deliveryTypeLabel(t, r.slug, r.name) || t("shipmentStats.noDeliveryType"),
                  fields: [
                    { label: t("shipmentStats.shipments"), render: (r) => r.shipments },
                    { label: t("shipmentStats.devices"), render: (r) => r.devices ?? 0 },
                  ],
                }}
              >
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t("shipmentStats.deliveryType")}</Table.Th>
                    <Table.Th>{t("shipmentStats.shipments")}</Table.Th>
                    <Table.Th>{t("shipmentStats.devices")}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {data.by_delivery_type.map((r) => (
                    <Table.Tr key={`${r.slug ?? ""}-${r.name ?? ""}`}>
                      <Table.Td>{deliveryTypeLabel(t, r.slug, r.name) || t("shipmentStats.noDeliveryType")}</Table.Td>
                      <Table.Td>{r.shipments}</Table.Td>
                      <Table.Td>{r.devices ?? 0}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
              </ResponsiveTable>
            </Card>
          )}

          {data.per_customer.length > 0 && (
            <Card withBorder>
              <Title order={5} mb="xs">{t("shipmentStats.perCustomer")}</Title>
              <ResponsiveTable
                mobile={{
                  rows: data.per_customer,
                  rowKey: (r) => String(r.customer_id),
                  title: (r) => r.customer_id ? (
                    <Anchor component={Link} to={`/customers/${r.customer_id}`} size="sm" fw={600}>
                      {r.customer_name}
                    </Anchor>
                  ) : (
                    <Text span size="sm" c="dimmed">{t("shipmentStats.internalStockOption")}</Text>
                  ),
                  fields: [
                    { label: t("shipmentStats.shipments"), render: (r) => r.shipments },
                    { label: t("shipmentStats.devices"), render: (r) => r.devices },
                    { label: t("shipmentStats.defects"), render: (r) => <Text span c={r.defects > 0 ? "red" : undefined}>{r.defects}</Text> },
                    { label: t("shipmentStats.returns"), render: (r) => <Text span c={r.returns > 0 ? "orange" : undefined}>{r.returns}</Text> },
                  ],
                }}
              >
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t("shipmentStats.customer")}</Table.Th>
                    <Table.Th>{t("shipmentStats.shipments")}</Table.Th>
                    <Table.Th>{t("shipmentStats.devices")}</Table.Th>
                    <Table.Th>{t("shipmentStats.defects")}</Table.Th>
                    <Table.Th>{t("shipmentStats.returns")}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {data.per_customer.map((r) => (
                    <Table.Tr key={String(r.customer_id)}>
                      <Table.Td>
                        {r.customer_id ? (
                          <Anchor component={Link} to={`/customers/${r.customer_id}`}>
                            {r.customer_name}
                          </Anchor>
                        ) : (
                          <Text c="dimmed">{t("shipmentStats.internalStockOption")}</Text>
                        )}
                      </Table.Td>
                      <Table.Td>{r.shipments}</Table.Td>
                      <Table.Td>{r.devices}</Table.Td>
                      <Table.Td>
                        <Text c={r.defects > 0 ? "red" : undefined}>{r.defects}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text c={r.returns > 0 ? "orange" : undefined}>{r.returns}</Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
              </ResponsiveTable>
            </Card>
          )}
        </Stack>
      )}
        </Tabs.Panel>

        <Tabs.Panel value="plan">
          <DeliveryPlanTab />
        </Tabs.Panel>

        <Tabs.Panel value="by-customer">
          <CustomerStatsTab />
        </Tabs.Panel>
      </Tabs>
    </Container>
  );
}

function StatCard({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <Card withBorder>
      <Text size="xs" c="dimmed">{label}</Text>
      <Text size="xl" fw={700} c={color}>{value}</Text>
    </Card>
  );
}
